import { useMutation } from "@tanstack/react-query";
import {
  useLocalizedApiQuery,
  useApiMutation,
  useApiQuery,
} from "../services/react-query";
import { notifications } from "@mantine/notifications";

export const useGetGiveawayByEventId = (eventId) =>
  useLocalizedApiQuery({
    queryOptions: {
      queryKey: ["giveaway", eventId],
      enabled: !!eventId,
    },
    fetchOptions: { url: "/giveaways", method: "GET", params: { eventId } },
  });

export const useCreateGiveaway = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "useCreateGiveaway" },
    fetchOptions: { url: "/giveaways", method: "POST" },
    invalidateQueryKeys: [["giveaway"]],
    successMessage: { title: "Giveaway has been created", color: "green" },
  });

export const useUpdateGiveaway = (id) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useUpdateGiveaway" },
    fetchOptions: { url: `/giveaways/${id}`, method: "PATCH" },
    invalidateQueryKeys: [["giveaway"], ["giveawayStats", id]],
    successMessage: {
      title: "Giveaway has been updated",
      color: "green",
    },
  });

export const useUpdateGiveawayStatus = (id) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useUpdateGiveawayStatus" },
    fetchOptions: { url: `/giveaways/${id}`, method: "PATCH" },
    invalidateQueryKeys: [["giveaway"], ["giveawayStats", id]],
    successMessage: {
      title: "Status has been updated",
      color: "green",
    },
  });

export const useDeleteGiveaway = (id, eventId) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useDeleteGiveaway" },
    fetchOptions: { url: `/giveaways/${id}`, method: "DELETE" },
    invalidateQueryKeys: [["giveaway", eventId]],
    successMessage: { title: "Giveaway has been deleted", color: "green" },
  });

export const useAddSponsorsToGiveaway = ({ id, eventId, ...params }) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useAddSponsorsToGiveaway" },
    fetchOptions: {
      url: `/giveaways/${id}/sponsors`,
      method: "POST",
      body: params,
    },
    invalidateQueryKeys: [
      ["giveaway", eventId],
      ["giveawaySponsorCodes"],
      ["giveawayStats"],
    ],
    successMessage: {
      title: "Sponsors have been added to giveaway",
      color: "green",
    },
  });

export const useRemoveSponsorFromGiveaway = ({ id, eventId, sponsorId }) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useRemoveSponsorFromGiveaway" },
    fetchOptions: {
      url: `/giveaways/${id}/sponsors/${sponsorId}`,
      method: "DELETE",
    },
    invalidateQueryKeys: [
      ["giveaway", eventId],
      ["giveawaySponsorCodes"],
      ["giveawayStats"],
    ],
    successMessage: {
      title: "Sponsor has been removed from giveaway",
      color: "green",
    },
  });

export const useGetGiveawayHistory = (giveawayId, params) =>
  useLocalizedApiQuery({
    queryOptions: {
      queryKey: ["giveawayHistory", giveawayId, params],
      enabled: !!giveawayId,
    },
    fetchOptions: {
      url: `/giveaways/${giveawayId}/history`,
      method: "GET",
      params,
    },
  });

export const useGetGiveawayParticipants = ({ id, ...params }) =>
  useLocalizedApiQuery({
    queryOptions: {
      queryKey: ["participants", id, params],
      enabled: !!id,
      refetchInterval: 3 * 60 * 1000,
    },
    fetchOptions: {
      url: `/giveaways/${id}/participants`,
      method: "GET",
      params,
    },
  });

export const useGetSponsorCodes = (id) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["giveawaySponsorCodes", id],
      enabled: !!id,
    },
    fetchOptions: {
      url: `/giveaways/${id}/sponsor-codes`,
      method: "GET",
    },
  });

export const useGetGiveawayStats = (id) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["giveawayStats", id],
      enabled: !!id,
    },
    fetchOptions: {
      url: `/giveaways/${id}/stats`,
      method: "GET",
    },
  });

export function useExportParticipants() {
  return useMutation({
    mutationFn: async ({ format, id, statusFilter }) => {
      const token = localStorage.getItem("token");

      const params = new URLSearchParams({ format });
      if (statusFilter && statusFilter !== "all") {
        params.append("statusFilter", statusFilter);
      }

      const response = await fetch(
        `/api/giveaways/${id}/export?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to export participants");
      }

      const contentDisposition = response.headers.get("Content-Disposition");
      let fileName = `giveaway-${id}-participants-${statusFilter || "all"}-${new Date().toISOString().split("T")[0].replace(/-/g, "")}.${format}`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1].replace(/^"|"$/g, "");
        }
      }

      return response.blob().then((blob) => ({
        blob,
        fileName,
      }));
    },
    onSuccess: ({ blob, fileName }) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      notifications.show({
        title: "Success",
        message: "Participants exported successfully",
        color: "green",
      });
    },
    onError: (error) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to export participants",
        color: "red",
      });
    },
  });
}
